import {
  Divider,
  Dropdown,
  DropdownOption,
  Row,
  RowSize,
  ValidationWrapper,
  WarningPanel,
} from "@/components";
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_LOADING_TEXT,
  DEFAULT_SELECT_PLACEHOLDER_TEXT,
} from "@/constants";
import { AuthContext } from "@/guards/auth/auth-context";
import { appConfig } from "@/guards/auth/azure-b2c.config";
import { useCompanies } from "@/pages/requests/components/contract-details-form/hooks/use-companies";
import { useContracts } from "@/pages/requests/components/contract-details-form/hooks/use-contracts";
import { useSubContractors } from "@/pages/requests/components/contract-details-form/hooks/use-subcontractors-type";
import {
  RequestResponse,
  requestDocumentBySubcontractorId,
} from "@/services/api";
import {
  selectRequestFormStore,
  selectUserData,
  useBoundStore,
} from "@/services/stores";
import { getBooleanConfigValue, logger } from "@/utils";
import { format, isPast } from "date-fns";
import { isValid } from "date-fns/isValid";
import { FC, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import styles from "./styles.scss";
import { ContractField, ContractFieldLabel } from "./types";
import { isCancel } from "axios";

export const ContractDetailsForm: FC<{
  data: RequestResponse | null;
}> = ({ data }) => {
  const { account } = useContext(AuthContext);

  const { requestFormData, setRequestFormData, setDocumentsInitialized } =
    useBoundStore(selectRequestFormStore);

  const lastFetchedSubcontractorIdRef = useRef<string | number | null>(null);
  const subcontractorDocAbortRef = useRef<AbortController | null>(null);

  const company = requestFormData?.[ContractField.Companies];
  const companyId = company?.Id;

  const { user } = useBoundStore(selectUserData);

  const {
    clearErrors,
    setValue,
    formState: { errors, isValid: isFormValid },
    trigger,
  } = useFormContext();

  if (getBooleanConfigValue(appConfig?.FEATURES?.IS_DEBUG_ENABLED)) {
    /* eslint-disable */
    console.log("ContractDetailsForm", errors, isFormValid);
    /* eslint-enable */
  }

  const LICENCE_IS_EXPIRED = useMemo(() => {
    const expDate =
      requestFormData?.[ContractField.ContractType]?.ValidityEndDate || "";

    const isValidDate = expDate ? isValid(new Date(expDate)) : false;

    if (isValidDate) {
      return {
        date: new Date(expDate),
        isExpired: isPast(new Date(expDate)),
      };
    }

    return {
      isExpired: false,
    };
  }, [requestFormData]);

  const SUBCONTRACTOR_LICENCE_IS_EXPIRED = useMemo(() => {
    const expDate =
      requestFormData?.[ContractField.SubContractor]?.TradeLicenseExpiryDate ||
      "";

    const isValidDate = expDate ? isValid(new Date(expDate)) : false;

    if (isValidDate) {
      return {
        date: new Date(expDate),
        isExpired: isPast(new Date(expDate)),
      };
    }

    return {
      isExpired: false,
    };
  }, [requestFormData]);

  const handleDataChange = useCallback(
    async (value: DropdownOption, dropdownId: ContractField) => {
      clearErrors(dropdownId);

      setValue(dropdownId, value);
      setRequestFormData(dropdownId, value);
      setDocumentsInitialized(false);

      await trigger(dropdownId);
    },
    [
      clearErrors,
      setRequestFormData,
      setValue,
      trigger,
      setDocumentsInitialized,
    ],
  );

  const handleClear = useCallback(
    (field: ContractField) => {
      setValue(field, undefined, { shouldValidate: true });
      setRequestFormData(field, undefined as unknown as DropdownOption);

      if (field === ContractField.SubContractor) {
        setDocumentsInitialized(false);
        lastFetchedSubcontractorIdRef.current = null;
      }

      clearErrors(field);
    },
    [clearErrors, setValue, setRequestFormData, setDocumentsInitialized],
  );

  const {
    isLoading: isLoadingCompanies,
    companies,
    error: companiesError,
  } = useCompanies();

  const {
    contracts,
    error: contractNumberError,
    isLoading: isLoadingContractNumber,
  } = useContracts({
    email: account?.username ?? "",
    contractorId: `${user?.Contractor?.ContractorId}`,
    companyId,
  });

  const {
    isLoading: isLoadingSubContractors,
    subContractors,
    error: subContractorsError,
  } = useSubContractors(account?.username ?? "");

  /**
   * Edit mode
   */
  useEffect(() => {
    if (data) {
      const { Lookups } = data;

      if (
        companies.length &&
        !requestFormData?.[ContractField.Companies] &&
        Lookups?.CompaniesLookup?.Companies?.length
      ) {
        handleDataChange(
          {
            ...Lookups?.CompaniesLookup?.Companies[0],
            Value: Lookups?.CompaniesLookup?.Companies[0].CompanyNameEn,
          },
          ContractField.Companies,
        );
      }

      if (
        contracts.length &&
        !requestFormData?.[ContractField.ContractType] &&
        Lookups?.LoaContractsLookup?.LoaContracts?.length
      ) {
        handleDataChange(
          {
            ...Lookups?.LoaContractsLookup?.LoaContracts?.[0],
            Value: Lookups?.LoaContractsLookup?.LoaContracts?.[0].ContractNo,
          },
          ContractField.ContractType,
        );
      }

      if (
        subContractors.length &&
        !requestFormData?.[ContractField.SubContractor]
      ) {
        handleDataChange(
          {
            ...Lookups?.LoaSubContractorLookup?.SubContractors?.[0],
            Value:
              Lookups?.LoaSubContractorLookup?.SubContractors?.[0]
                .CompanyNameEn,
          },
          ContractField.SubContractor,
        );
      }
    }
  }, [
    companies,
    contracts,
    data,
    handleDataChange,
    requestFormData,
    subContractors,
  ]);

  useEffect(() => {
    const subcontractor = requestFormData?.[ContractField.SubContractor];

    if (!subcontractor) {
      if (subcontractorDocAbortRef.current) {
        subcontractorDocAbortRef.current.abort();
        subcontractorDocAbortRef.current = null;
      }

      return;
    }

    const subcontractorId =
      subcontractor.SubContractorId || subcontractor.Id || null;

    if (!subcontractorId) {
      return;
    }

    if (
      lastFetchedSubcontractorIdRef.current === subcontractorId ||
      subcontractor.TradeLicenseDocument
    ) {
      return;
    }

    lastFetchedSubcontractorIdRef.current = subcontractorId;

    if (subcontractorDocAbortRef.current) {
      subcontractorDocAbortRef.current.abort();
    }

    const controller = new AbortController();
    subcontractorDocAbortRef.current = controller;

    requestDocumentBySubcontractorId(
      {
        subcontractorId: String(subcontractorId),
      },
      { signal: controller.signal },
    )
      .then(document => {
        if (!document || isCancel(document)) {
          return;
        }

        setRequestFormData(ContractField.SubContractor, {
          ...subcontractor,
          TradeLicenseDocument: document,
        });
      })
      .catch(() => {
        const error = "Can't find subcontractor TradeLicenseDocument";
        logger.error(error);
      })
      .finally(() => {
        if (subcontractorDocAbortRef.current === controller) {
          subcontractorDocAbortRef.current = null;
        }
      });
  }, [requestFormData, setRequestFormData]);

  return (
    <>
      <Row size={RowSize.Small}>
        <ValidationWrapper
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          errorMessage={
            companiesError ||
            (errors[ContractField.Companies]?.message as string)
          }
        >
          <Dropdown
            containerClassName={styles.dropdownContainer}
            id={ContractField.Companies}
            label={ContractFieldLabel.Companies}
            placeholder={
              isLoadingCompanies
                ? DEFAULT_LOADING_TEXT
                : DEFAULT_SELECT_PLACEHOLDER_TEXT
            }
            selectedOption={requestFormData?.[ContractField.Companies]}
            options={companies}
            isNeedToHighlightSelectedItem
            onChange={handleDataChange}
            onFocus={() => {
              clearErrors(ContractField.Companies);
            }}
            disabled={!companies.length}
            mandatory
          />
        </ValidationWrapper>
      </Row>

      <Row size={RowSize.Small}>
        <ValidationWrapper
          errorMessage={
            (contractNumberError as string | undefined) ||
            (errors[ContractField.ContractType]?.message as string)
          }
        >
          <Dropdown
            containerClassName={styles.dropdownContainer}
            id={ContractField.ContractType}
            label={ContractFieldLabel.ContractNumber}
            placeholder={
              isLoadingContractNumber
                ? DEFAULT_LOADING_TEXT
                : DEFAULT_SELECT_PLACEHOLDER_TEXT
            }
            selectedOption={requestFormData?.[ContractField.ContractType]}
            options={contracts}
            isNeedToHighlightSelectedItem
            onChange={handleDataChange}
            onFocus={() => {
              clearErrors(ContractField.ContractType);
            }}
            disabled={!requestFormData?.[ContractField.Companies]}
            mandatory
            searchable={true}
            onClear={handleClear}
          />
        </ValidationWrapper>
      </Row>

      {requestFormData?.[ContractField.ContractType]?.Employee && (
        <Row size={RowSize.Small}>
          <div className={styles.contractAdmin}>
            <span className={styles.contractAdminLabel}>
              Contract Administrator
            </span>
            <span>
              {requestFormData?.[ContractField.ContractType]?.Employee?.NameEn}
            </span>
          </div>
        </Row>
      )}

      {LICENCE_IS_EXPIRED.isExpired && (
        <WarningPanel
          title="Licence Exp. Date"
          text={
            LICENCE_IS_EXPIRED.date
              ? format(LICENCE_IS_EXPIRED.date, DEFAULT_DATE_FORMAT)
              : ""
          }
        />
      )}

      <Row size={RowSize.ExtraLarge}>
        <Divider />
      </Row>

      <Row size={RowSize.Small}>
        <ValidationWrapper
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          errorMessage={
            subContractorsError ||
            (errors[ContractField.SubContractor]?.message as string)
          }
          helperMessage="Select subcontractor if needed"
        >
          <Dropdown
            containerClassName={styles.dropdownContainer}
            id={ContractField.SubContractor}
            label={ContractFieldLabel.SubContractor}
            placeholder={
              isLoadingSubContractors
                ? DEFAULT_LOADING_TEXT
                : DEFAULT_SELECT_PLACEHOLDER_TEXT
            }
            selectedOption={requestFormData?.[ContractField.SubContractor]}
            options={subContractors}
            isNeedToHighlightSelectedItem
            onChange={handleDataChange}
            onFocus={() => {
              clearErrors(ContractField.SubContractor);
            }}
            disabled={!subContractors.length}
            searchable={true}
            onClear={handleClear}
          />
        </ValidationWrapper>
      </Row>

      {SUBCONTRACTOR_LICENCE_IS_EXPIRED.isExpired && (
        <WarningPanel
          title="Licence Exp. Date"
          text={
            SUBCONTRACTOR_LICENCE_IS_EXPIRED.date
              ? format(
                  SUBCONTRACTOR_LICENCE_IS_EXPIRED.date,
                  DEFAULT_DATE_FORMAT,
                )
              : ""
          }
        />
      )}

      <Row size={RowSize.ExtraLarge}>
        <Divider />
      </Row>
    </>
  );
};
